// Rychlý náhled produktu z upsellu „Hodí se k tomu:" na detailu (zadání Tomáše
// 2026-09-12). Blok pod tlačítkem do košíku nabízí tři související produkty
// skladem; zákazník o nich věděl jen název, miniaturu a cenu, a proklik na
// jejich detail ho odváděl z hlavního produktu. Klik na FOTKU nebo NÁZEV proto
// otevírá modal ve stejné řeči jako „Možnosti doručení" — fotky, cena,
// dostupnost, krátký popis a parametry — a tlačítko v patičce položku rovnou
// zaškrtne k objednávce.
//
// Rozhodnutí Tomáše:
// 1. Trigger je jen fotka a název. Zbytek řádku (checkbox, cena, prázdná
//    plocha) přepíná výběr dál. ⚠️ Vědomá odchylka od
//    `design_handoff_product/README.md` §upsell („klik kamkoli na řádek
//    přepíná výběr") — narazí-li budoucí session na rozdíl proti handoveru,
//    NEVRACET.
// 2. Tlačítko v popupu jen ZAŠKRTNE položku k objednávce (přepne checkbox
//    v řádku), nepřidává ji do košíku samo — žádný souboj s modalem „Přidáno
//    do košíku" (cartModal.js). Do košíku jde s hlavním produktem.
//
// Zdroj dat je HTML detailu toho produktu (productPreviewModel.js), rám
// a chování dialogu dodává modalShell.js (sdílený s modalem dopravy a servisu).
//
// Progressive enhancement: odkaz vede na detail sám; na `file://`, bez fetch
// a při kliku s modifikátorem necháváme nativní navigaci — stejné pravidlo jako
// shippingModal.js / serviceModal.js.

import { icon } from './icon.js';
import { createModal, shellNode, shellClass } from './modalShell.js';
import { formatPrice, parseNumber, discountPercent } from './productPrice.js';
import { loadProductPreview } from './productPreviewModel.js';
import { NBSP } from './money.js';
import L from '../locale/cs/upsellQuickView.js';

const PREFIX = 'bs-qv';

const TITLE = L.title;
const CLOSE_LABEL = L.closeLabel;
const DONE_LABEL = L.doneLabel;
const PAGE_LABEL = L.pageLabel;
const LABEL_LOADING = L.loading;
const LABEL_ERROR = L.error;
const LABEL_ADD = L.add;
const LABEL_ADDED = L.added;
const LABEL_VARIANT = L.variant;
const LABEL_PARAMS = L.params;
const LABEL_PARAMS_ALL = L.paramsAll;
const LABEL_RATING = L.rating;
const LABEL_DELIVERY = L.delivery;
const LABEL_PHOTO = L.photo;

// Kolik řádků parametrů stojí rovnou v těle; zbytek schová nativní <details>
const PARAMS_VISIBLE = 6;
// Prefetch až když kurzor nad řádkem chvíli zůstane (přejetí myší nestahuje)
const PREFETCH_DELAY_MS = 150;
// Po zaškrtnutí z CTA se modal zavírá smám — prodleva nechá probliknout zelený
// stav „Přidáno k objednávce", jinak by potvrzení stálo jen na zaškrtnutém řádku
// a vyšší ceně v CTA detailu (rozhodnutí Tomáše 2026-09-12)
const CLOSE_AFTER_ADD_MS = 450;

const node = shellNode;

// Aktuálně otevřený produkt — drží se `input` řádku, ne kopie stavu výběru
let current = null;
let modal = null;
let ctaBtn = null;
let closeTimer = null;

// `opts` drží shell po celou dobu a `build()` z něj staví DOM při KAŽDÉM
// `open()` — podtitulek per produkt se proto přepisuje před otevřením.
const MODAL_OPTS = {
  prefix: PREFIX,
  icon: 'eye',
  title: TITLE,
  sub: '',
  closeLabel: CLOSE_LABEL,
  doneLabel: DONE_LABEL,
  link: {
    label: PAGE_LABEL,
    href: function () {
      return current ? current.href : '#';
    },
  },
  onClose: function () {
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
    if (current && current.input) current.input.removeEventListener('change', syncCta);
    current = null;
    ctaBtn = null;
  },
};

// --- patička: zaškrtnutí položky -------------------------------------------

/** Stav CTA se čte VÝHRADNĚ z `input.checked` — žádná druhá kopie výběru. */
function syncCta() {
  if (!ctaBtn || !current) return;
  const on = !!(current.input && current.input.checked);
  ctaBtn.textContent = '';
  ctaBtn.classList.toggle('is-added', on);
  ctaBtn.appendChild(icon(on ? 'check' : 'cart'));
  const label = on ? LABEL_ADDED : LABEL_ADD + ' · +' + NBSP + formatPrice(current.price);
  ctaBtn.appendChild(node('span', null, label));
  ctaBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
}

function buildCta(ui, data) {
  // Variantu kódem do košíku přidat nejde — CTA nahradí odkaz na detail.
  // (Do upsellu se variantní karta nedostane, `buildUpsell` je vyřazuje podle
  // chybějícího `form.pr-action`; tohle je pojistka, kdyby se markup změnil.)
  if (data && data.hasVariants) {
    const a = node('a', PREFIX + '__cta ' + PREFIX + '__cta--variant');
    a.href = current.href;
    a.appendChild(icon('arrow-right'));
    a.appendChild(node('span', null, LABEL_VARIANT));
    ui.foot.appendChild(a);
    return;
  }
  if (!current.input) return;
  ctaBtn = node('button', PREFIX + '__cta');
  ctaBtn.type = 'button';
  ctaBtn.addEventListener('click', function () {
    // `click()` na nativním checkboxu vystřelí `change` → `it.selected`,
    // `is-selected`, cena v CTA detailu i event `bs:pd-extras`
    current.input.click();
    // Zavírá se JEN při přechodu do zaškrtnuto a JEN z tohohle tlačítka:
    // `syncCta` běží i při otevření nad předem vybraným řádkem (to by modal
    // zavřelo dřív, než by šel vidět) a při přepnutí checkboxu zvenku.
    // Odškrtnutí druhým klikem modal nechává otevřený.
    if (!current.input.checked) return;
    if (closeTimer) clearTimeout(closeTimer);
    closeTimer = setTimeout(function () {
      closeTimer = null;
      if (modal && modal.isOpen()) modal.close();
    }, CLOSE_AFTER_ADD_MS);
  });
  current.input.addEventListener('change', syncCta);
  ui.foot.appendChild(ctaBtn);
  syncCta();
}

// --- tělo -------------------------------------------------------------------

function buildGallery(images) {
  const box = node('div', PREFIX + '__gallery');
  const stage = node('div', PREFIX + '__stage');
  const img = node('img', PREFIX + '__stageimg');
  img.src = images[0].full;
  img.alt = images[0].alt || '';
  stage.appendChild(img);
  box.appendChild(stage);
  if (images.length > 1) {
    const strip = node('div', PREFIX + '__thumbs');
    images.forEach(function (it, i) {
      const btn = node('button', PREFIX + '__thumb' + (i === 0 ? ' is-current' : ''));
      btn.type = 'button';
      btn.setAttribute('aria-label', LABEL_PHOTO + ' ' + (i + 1));
      const t = node('img');
      t.src = it.thumb;
      t.alt = '';
      t.loading = 'lazy';
      btn.appendChild(t);
      btn.addEventListener('click', function () {
        img.src = it.full;
        img.alt = it.alt || '';
        [...strip.children].forEach(function (el) {
          el.classList.toggle('is-current', el === btn);
        });
      });
      strip.appendChild(btn);
    });
    box.appendChild(strip);
  }
  return box;
}

/** Hvězdy sdíleným primitivem `.bs-stars` (_stars.scss), jako detail produktu. */
function buildStars(value) {
  const wrap = node('span', 'bs-stars ' + PREFIX + '__stars', '★★★★★');
  wrap.setAttribute('aria-hidden', 'true');
  wrap.style.setProperty('--bs-stars-pct', Math.round((value / 5) * 1000) / 10 + '%');
  wrap.appendChild(node('span', 'bs-stars__fill', '★★★★★'));
  return wrap;
}

function buildMeta(data) {
  if (!data || (!data.rating && !data.brand)) return null;
  const row = node('div', PREFIX + '__meta');
  if (data.rating) {
    row.appendChild(buildStars(data.rating.value));
    row.appendChild(node('span', PREFIX + '__ratingcount', data.rating.count + NBSP + LABEL_RATING));
  }
  if (data.brand) {
    if (data.rating) row.appendChild(node('span', PREFIX + '__sep', '·'));
    row.appendChild(node('span', PREFIX + '__brand', data.brand));
  }
  return row;
}

/** Cena: hlavní částka je VŽDY cena z řádku upsellu (ta jde do součtu CTA
 *  detailu); fetch doplňuje jen škrtnutou, slevu a cenu bez DPH.
 *
 *  Škrtnutá cena se kreslí jen tehdy, když je opravdu VYŠŠÍ než zobrazená —
 *  řádek a fetchnutý detail se mohou rozejít (velkoobchodní ceník, cache stránky)
 *  a „679 Kč / přeškrtnuté 1 699 Kč" u nesouhlasících čísel lže. Procento se počítá
 *  z těch DVOU čísel, která zákazník vidí (floor jako Shoptet), ne z detailu. */
function buildPrice(data) {
  const box = node('div', PREFIX + '__pricebox');
  const line = node('div', PREFIX + '__priceline');
  line.appendChild(node('strong', PREFIX + '__price', formatPrice(current.price)));
  const before = data ? parseNumber(data.priceBefore) : NaN;
  const pct = isFinite(before) && before > current.price ? discountPercent(before, current.price) : null;
  if (pct !== null) {
    line.appendChild(node('span', PREFIX + '__was', data.priceBefore));
    // pozor: `__badge` je v shellu medailonek hlavičky (shellClass) — jmenuje se `__save`
    line.appendChild(node('span', PREFIX + '__save', '−' + pct + NBSP + '%'));
  }
  box.appendChild(line);
  if (data && data.priceNoVat) box.appendChild(node('span', PREFIX + '__novat', data.priceNoVat));
  return box;
}

function buildStatus(data) {
  const box = node('div', PREFIX + '__status');
  const avail = node('span', PREFIX + '__avail');
  avail.appendChild(icon('check'));
  const label = data && data.availability && data.availability.label ? data.availability.label : current.stock;
  avail.appendChild(node('span', null, data && data.availability && data.availability.amount ? label + ' ' + data.availability.amount : label));
  box.appendChild(avail);
  if (data && data.delivery) {
    const eta = node('span', PREFIX + '__eta');
    eta.appendChild(icon('calendar'));
    eta.appendChild(node('span', null, LABEL_DELIVERY + ' ' + data.delivery));
    box.appendChild(eta);
  }
  return box;
}

function buildDesc(data) {
  // Popis v2 má claim + perex, starší produkt jen krátký popis z administrace
  const intro = data.intro && (data.intro.claim || data.intro.text) ? data.intro : null;
  if (!intro && !data.shortDesc.length) return null;
  const box = node('div', PREFIX + '__desc');
  if (intro) {
    if (intro.claim) box.appendChild(node('strong', PREFIX + '__claim', intro.claim));
    if (intro.text) box.appendChild(node('p', PREFIX + '__p', intro.text));
    return box;
  }
  let list = null;
  data.shortDesc.forEach(function (item) {
    if (item.tag === 'li') {
      if (!list) {
        list = node('ul', PREFIX + '__bullets');
        box.appendChild(list);
      }
      list.appendChild(node('li', null, item.text));
      return;
    }
    list = null;
    box.appendChild(node('p', PREFIX + '__p', item.text));
  });
  return box;
}

function buildKeyspecs(data) {
  if (!data.keyspecs.length) return null;
  const box = node('div', PREFIX + '__keyspecs');
  data.keyspecs.forEach(function (k) {
    const chip = node('span', PREFIX + '__keyspec');
    chip.appendChild(node('strong', null, k.value));
    chip.appendChild(node('span', null, k.label));
    box.appendChild(chip);
  });
  return box;
}

function paramTable(rows) {
  const table = node('table', PREFIX + '__table');
  const body = node('tbody');
  rows.forEach(function (p) {
    const tr = node('tr');
    tr.appendChild(node('th', null, p.label));
    tr.appendChild(node('td', null, p.value));
    body.appendChild(tr);
  });
  table.appendChild(body);
  return table;
}

function buildParams(data) {
  const rows = data.params.rows;
  if (!rows.length) return null;
  const box = node('section', PREFIX + '__params');
  box.appendChild(node('strong', PREFIX + '__subtitle', LABEL_PARAMS));
  box.appendChild(paramTable(rows.slice(0, PARAMS_VISIBLE)));
  if (rows.length > PARAMS_VISIBLE) {
    // nativní <details>, žádný JS — stejné pravidlo jako FAQ šablony popisu
    const more = node('details', PREFIX + '__more');
    more.appendChild(node('summary', null, LABEL_PARAMS_ALL + ' (' + rows.length + ')'));
    more.appendChild(paramTable(rows.slice(PARAMS_VISIBLE)));
    box.appendChild(more);
  }
  return box;
}

/** Seed z dat řádku — vidět hned, než doběhne fetch. */
function seed(ui) {
  ui.body.textContent = '';
  const cols = node('div', PREFIX + '__cols');
  if (current.img) {
    cols.appendChild(buildGallery([{ full: current.img, thumb: current.img, alt: current.name }]));
  }
  const info = node('div', PREFIX + '__info');
  info.appendChild(node('h3', PREFIX + '__name', current.name));
  info.appendChild(buildPrice(null));
  info.appendChild(buildStatus(null));
  info.appendChild(node('p', shellClass(PREFIX, 'msg'), LABEL_LOADING));
  cols.appendChild(info);
  ui.body.appendChild(cols);
}

function fill(ui, data) {
  // výpadek: seed zůstává, jen se vymění hláška — odkaz na detail nese patička
  if (!data) {
    const msg = ui.body.querySelector('.' + PREFIX + '__msg');
    if (msg) msg.textContent = LABEL_ERROR;
    buildCta(ui, null);
    return;
  }

  ui.body.textContent = '';
  const cols = node('div', PREFIX + '__cols');
  if (data.images.length) cols.appendChild(buildGallery(data.images));
  else if (current.img) cols.appendChild(buildGallery([{ full: current.img, thumb: current.img, alt: data.name }]));

  const info = node('div', PREFIX + '__info');
  info.appendChild(node('h3', PREFIX + '__name', data.name || current.name));
  const meta = buildMeta(data);
  if (meta) info.appendChild(meta);
  info.appendChild(buildPrice(data));
  info.appendChild(buildStatus(data));
  const desc = buildDesc(data);
  if (desc) info.appendChild(desc);
  const keyspecs = buildKeyspecs(data);
  if (keyspecs) info.appendChild(keyspecs);
  const params = buildParams(data);
  if (params) info.appendChild(params);
  cols.appendChild(info);
  ui.body.appendChild(cols);

  // podtitulek hlavičky: kód · značka
  const sub = ui.root.querySelector('.' + PREFIX + '__sub');
  if (sub) sub.textContent = [data.code || current.code, data.brand].filter(Boolean).join(' · ');

  buildCta(ui, data);
}

// --- napojení ---------------------------------------------------------------

function openFor(trigger) {
  const row = trigger.closest('.bs-buy__acc');
  if (!row) return;
  const thumb = row.querySelector('.bs-buy__acc-img img');
  current = {
    href: trigger.getAttribute('href'),
    input: row.querySelector('.bs-buy__acc-input'),
    code: row.dataset.bsCode || '',
    name: row.dataset.bsName || '',
    price: parseFloat(row.dataset.bsPrice) || 0,
    stock: row.dataset.bsStock || '',
    img: thumb ? thumb.getAttribute('src') : '',
  };
  MODAL_OPTS.sub = current.code;
  // Safari po kliku anchor nefokusuje — bez toho by shell neměl na co vrátit fokus
  trigger.focus();
  const ui = modal.open();
  if (!ui) return;
  ui.done.hidden = true;
  seed(ui);
  const href = current.href;
  loadProductPreview(href).then(function (data) {
    if (modal.isOpen() && current && current.href === href) fill(ui, data);
  });
}

/** Prefetch detailu, když kurzor nad řádkem chvíli zůstane. Jen na zařízeních
 *  s kurzorem — na dotyku by stahoval naslepo. Váže se na řádky, ne na
 *  dokument: `pointerenter` se dispatchuje na každý najetý prvek, takže
 *  delegát v capture fázi by běžel při každém pohybu myši po stránce. */
function bindPrefetch() {
  if (!(window.matchMedia && window.matchMedia('(hover: hover)').matches)) return;
  [...document.querySelectorAll('.bs-buy__upsell .bs-buy__acc')].forEach(function (row) {
    if (row.dataset.bsQvPre) return;
    row.dataset.bsQvPre = '1';
    let timer = null;
    row.addEventListener('pointerenter', function () {
      const link = row.querySelector('a[data-bs-qv]');
      if (!link || timer) return;
      timer = setTimeout(function () {
        loadProductPreview(link.getAttribute('href'));
      }, PREFETCH_DELAY_MS);
    });
    row.addEventListener('pointerleave', function () {
      if (timer) clearTimeout(timer);
      timer = null;
    });
  });
}

export function initUpsellQuickView() {
  // řádky upsellu se při re-initu detailu staví znovu → prefetch se váže vždy
  bindPrefetch();
  // idempotence: modal visí na dokumentu, ne na uzlu detailu
  if (document.documentElement.dataset.bsQv) return;
  document.documentElement.dataset.bsQv = '1';

  modal = createModal(MODAL_OPTS);

  document.addEventListener(
    'click',
    function (e) {
      const target = e.target instanceof Element ? e.target.closest('a[data-bs-qv]') : null;
      if (!target) return;
      // na file:// (offline fixtures) i bez fetch necháváme nativní navigaci,
      // klik s modifikátorem = zákazník chce detail v novém okně
      if (!window.fetch || location.protocol === 'file:') return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      // `preventDefault` zároveň ruší aktivaci obalového `<label>`, takže se
      // klikem na fotku ani název NEPŘEPÍNÁ výběr (rozhodnutí Tomáše)
      e.preventDefault();
      e.stopPropagation();
      openFor(target);
    },
    true
  );
}
